import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { Close, ContentCopy, Delete, Edit, Gavel, NoteAdd } from "@mui/icons-material";
import HeaderInternal from "../components/Header/HeaderInternal";
import { FooterInternal } from "../components/Footer";
import { FaqButton } from "../components/FaqButton";
import AddButton from "../components/AddButton";
import ClearFiltersButton from "../components/ClearFiltersButton";
import ResultCount from "../components/ResultCount";
import ConfirmModal from "../components/modals/ConfirmModal";
import SavingOverlay from "../components/SavingOverlay";
import { Toast } from "../components/Toast";
import { useToast } from "../hooks/useToast";
import { useNavigation, useRouter } from "../contexts/RouterContext";
import { useAuth } from "../contexts/AuthContext";
import { colors, typography, pageHeader } from "../theme/designSystem";
import {
  contractErrorMessage,
  contractTemplatesService,
  ContractTemplateAudience,
  ContractTemplateRow,
  ContractTemplateVersionRow,
  CONTRACT_STATUS_LABELS,
  CONTRACT_TEMPLATE_AUDIENCE_LABELS,
} from "../services/contractTemplatesService";

/**
 * Prontuário e documentos → Modelos de contrato: a lista das famílias, cada uma com a versão publicada
 * (a que a emissão usa) e o rascunho em andamento. Abrir uma versão leva ao editor (página-filha
 * `contract-template-editor`, por ?versionId=). Mesmo esqueleto das listas canônicas: filtros reativos,
 * cartões no celular, modal canônico para título e descrição.
 */

interface HeaderForm {
  id: string | null;
  title: string;
  description: string;
  /** destinatário: só se escolhe na criação; depois, só muda enquanto nenhum contrato foi emitido (pelo editor) */
  audience: ContractTemplateAudience;
  active: boolean;
}

const MAX_TITLE = 120;
const MAX_DESCRIPTION = 500;

const STATUS_COLORS: Record<ContractTemplateVersionRow["status"], { bg: string; fg: string }> = {
  draft: { bg: colors.warningSoft, fg: colors.warningSoftText },
  published: { bg: colors.successSoft, fg: colors.successHover },
  archived: { bg: colors.backgroundAlt, fg: colors.textSecondary },
};

const formatDate = (iso: string | null) => (iso ? new Date(iso).toLocaleDateString("pt-BR") : "");

const rowActionSx = (background: string, hover: string) => ({
  backgroundColor: background,
  color: colors.white,
  "&:hover": { backgroundColor: hover },
});

const ContractTemplates: React.FC = () => {
  const { user } = useAuth();
  const { goToDashboard } = useNavigation();
  const { navigateTo } = useRouter();
  const { toast, showToast, hideToast } = useToast();

  const [rows, setRows] = useState<ContractTemplateRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState<"all" | "active" | "inactive">("all");

  const [form, setForm] = useState<HeaderForm | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<ContractTemplateRow | null>(null);
  const [duplicateTarget, setDuplicateTarget] = useState<ContractTemplateRow | null>(null);
  const [duplicateTitle, setDuplicateTitle] = useState("");
  const [saving, setSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      setRows(await contractTemplatesService.getAll());
    } catch {
      showToast("Erro ao carregar os modelos de contrato.", "error");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    document.title = "Clinic4Us - Modelos de contrato";
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const normalize = (v: string) => v.normalize("NFD").replace(/[̀-ͯ]/g, "").toLowerCase();
  const filtered = useMemo(() => {
    const term = normalize(search.trim());
    return rows
      .filter((r) => statusFilter === "all" || (statusFilter === "active" ? r.active : !r.active))
      .filter((r) => !term || normalize(`${r.title} ${r.description ?? ""}`).includes(term));
  }, [rows, search, statusFilter]);

  const hasFilterChanges = search !== "" || statusFilter !== "all";
  const clearFilters = () => { setSearch(""); setStatusFilter("all"); };

  const openCreate = () => setForm({ id: null, title: "", description: "", audience: "patient", active: true });
  // Editar = o editor completo (título, descrição, texto, campos e versões): abre o rascunho, se
  // houver; senão a publicada; senão a mais recente. O modal fica só para criar.
  const openEditor = (row: ContractTemplateRow) => {
    const target = row.versions.find((v) => v.status === "draft") ?? row.versions.find((v) => v.status === "published") ?? row.versions[0];
    if (target) navigateTo("contract-template-editor", { versionId: target.id });
  };

  const saveHeader = async () => {
    if (!form) return;
    const title = form.title.trim();
    if (!title) { showToast("Informe o título do modelo.", "error"); return; }
    setSaving(true);
    try {
      const saved = await contractTemplatesService.save({
        id: form.id,
        title,
        description: form.description.trim() || null,
        audience: form.id ? undefined : form.audience,
        active: form.active,
      });
      setForm(null);
      if (!form.id && saved.draftVersionId) {
        // Modelo novo nasce com a v1 em rascunho: vai direto para o texto
        navigateTo("contract-template-editor", { versionId: saved.draftVersionId });
        return;
      }
      showToast("Modelo atualizado.", "success");
      await load();
    } catch (e) {
      showToast(contractErrorMessage(e, "Erro ao salvar o modelo."), "error");
    } finally {
      setSaving(false);
    }
  };

  const toggleActive = async (row: ContractTemplateRow) => {
    try {
      await contractTemplatesService.save({ id: row.id, title: row.title, description: row.description, active: !row.active });
      await load();
    } catch (e) {
      showToast(contractErrorMessage(e, "Erro ao alterar o modelo."), "error");
    }
  };

  const newVersion = async (row: ContractTemplateRow) => {
    setSaving(true);
    try {
      const id = await contractTemplatesService.createVersion(row.id);
      navigateTo("contract-template-editor", { versionId: id });
    } catch (e) {
      showToast(contractErrorMessage(e, "Erro ao criar a nova versão."), "error");
      setSaving(false);
    }
  };

  // Duplicar = modelo novo, sem vínculo com a origem (Nova versão é o que fica na família)
  const openDuplicate = (row: ContractTemplateRow) => {
    setDuplicateTitle(`${row.title} (cópia)`);
    setDuplicateTarget(row);
  };

  const confirmDuplicate = async () => {
    if (!duplicateTarget) return;
    const title = duplicateTitle.trim();
    if (!title) { showToast("Informe o título do novo modelo.", "error"); return; }
    setSaving(true);
    try {
      const copy = await contractTemplatesService.duplicate(duplicateTarget.id, title);
      setDuplicateTarget(null);
      if (copy.draftVersionId) {
        navigateTo("contract-template-editor", { versionId: copy.draftVersionId });
        return;
      }
      await load();
    } catch (e) {
      showToast(contractErrorMessage(e, "Erro ao duplicar o modelo."), "error");
    } finally {
      setSaving(false);
    }
  };

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    setSaving(true);
    try {
      await contractTemplatesService.remove(deleteTarget.id);
      showToast("Modelo excluído.", "success");
      setDeleteTarget(null);
      await load();
    } catch (e) {
      showToast(contractErrorMessage(e, "Erro ao excluir o modelo."), "error");
    } finally {
      setSaving(false);
    }
  };

  const openVersion = (version: ContractTemplateVersionRow) =>
    navigateTo("contract-template-editor", { versionId: version.id });

  const versionChip = (version: ContractTemplateVersionRow) => {
    const palette = STATUS_COLORS[version.status];
    const when = version.status === "published" ? formatDate(version.publishedAt) : formatDate(version.createdDate);
    return (
      <Tooltip
        key={version.id}
        arrow
        title={`${CONTRACT_STATUS_LABELS[version.status]} em ${when}${version.changeNote ? ` · ${version.changeNote}` : ""} — clique para abrir`}
      >
        <Chip
          label={`v${version.version} · ${CONTRACT_STATUS_LABELS[version.status]}`}
          size="small"
          onClick={() => openVersion(version)}
          sx={{ height: 22, fontSize: "0.7rem", fontWeight: 700, cursor: "pointer", backgroundColor: palette.bg, color: palette.fg }}
        />
      </Tooltip>
    );
  };

  return (
    <Box sx={{ display: "flex", flexDirection: "column", minHeight: "100vh" }}>
      <HeaderInternal
        showCTAButton={false}
        className="login-header"
        isLoggedIn={true}
        userEmail={user?.email || ""}
        userProfile={user?.role || ""}
        clinicName={user?.clinicName || "Clínica"}
        notificationCount={0}
        onRevalidateLogin={() => {}}
        onNotificationClick={() => {}}
        onUserClick={() => {}}
        onLogoClick={goToDashboard}
      />

      <Box component="main" sx={{ padding: "1rem", minHeight: "calc(100vh - 120px)", background: colors.background, marginTop: "85px", flex: 1 }}>
        <Container maxWidth={false} disableGutters sx={pageHeader.container}>
          <Box className="c4u-page-header c4u-page-header--in-panel">
            <Box>
              <Typography variant="h4" className="c4u-page-header__title">Modelos de contrato</Typography>
              <Typography variant="body2" className="c4u-page-header__subtitle">
                Os contratos que a clínica emite para os pacientes, com versões: a publicada é a que vale; mudança vira nova versão.
              </Typography>
            </Box>
            <Box className="c4u-page-header__actions">
              <FaqButton />
            </Box>
          </Box>

          <div className="c4u-panel">
            <div className="c4u-filters__row">
              <div className="c4u-filters__fields">
                <TextField
                  size="small"
                  label="Buscar"
                  placeholder="Título ou descrição"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  inputProps={{ maxLength: 100 }}
                  InputLabelProps={{ shrink: true }}
                  fullWidth
                />
                <FormControl size="small" fullWidth>
                  <InputLabel shrink>Situação</InputLabel>
                  <Select label="Situação" value={statusFilter} onChange={(e) => setStatusFilter(e.target.value as typeof statusFilter)} notched displayEmpty>
                    <MenuItem value="all">Todos</MenuItem>
                    <MenuItem value="active">Ativos</MenuItem>
                    <MenuItem value="inactive">Inativos</MenuItem>
                  </Select>
                </FormControl>
              </div>
              <div className="c4u-filters__actions">
                <ClearFiltersButton onClick={clearFilters} disabled={!hasFilterChanges} />
                <AddButton onClick={openCreate} title="Novo modelo de contrato" />
              </div>
            </div>

            {loading ? (
              <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
                <CircularProgress sx={{ color: colors.primary }} />
              </Box>
            ) : filtered.length === 0 ? (
              <Box sx={{ textAlign: "center", py: 6, color: colors.textSecondary }}>
                <Gavel sx={{ fontSize: 48, color: "#ced4da", mb: 1 }} />
                <Typography variant="body1" sx={{ mb: 0.5, color: colors.textPrimary }}>Nenhum modelo de contrato no filtro</Typography>
                <Typography variant="body2">Ajuste o filtro ou inclua um modelo novo.</Typography>
              </Box>
            ) : (
              <div>
                <ResultCount shown={filtered.length} total={rows.length} />
                <Box className="c4u-table__header">
                  <Box className="admin-plans-header-cell" sx={{ flex: "3 1 260px", minWidth: 0 }}>Modelo</Box>
                  <Box className="admin-plans-header-cell" sx={{ flex: "2 1 200px", minWidth: 0 }}>Versões</Box>
                  <Box className="admin-plans-header-cell c4u-col--md" sx={{ flex: "0 0 180px" }}>Cadastrado por</Box>
                  <Box className="admin-plans-header-cell" sx={{ flex: "0 0 90px" }}>Situação</Box>
                  <Box className="admin-plans-header-cell" sx={{ flex: "0 0 170px", justifyContent: "flex-end" }}>Ações</Box>
                </Box>

                {filtered.map((row) => {
                  const hasDraft = row.versions.some((v) => v.status === "draft");
                  const hasPublished = row.versions.some((v) => v.status !== "draft");
                  return (
                    <Box key={row.id} className="c4u-table__row" sx={{ opacity: row.active ? 1 : 0.65 }}>
                      {/* A célula é flex (padrão da tabela): título e descrição precisam de um bloco próprio para empilhar */}
                      <Box className="admin-plans-cell" data-label="Modelo" sx={{ flex: "3 1 260px", minWidth: 0 }}>
                        <Box sx={{ minWidth: 0 }}>
                          <Typography
                            component="button"
                            type="button"
                            onClick={() => openEditor(row)}
                            title="Abrir o modelo (texto, campos e versões)"
                            sx={{ fontWeight: 600, fontSize: "0.875rem", color: colors.textPrimary, wordBreak: "break-word", lineHeight: 1.3, background: "none", border: "none", padding: 0, cursor: "pointer", textAlign: "left", font: "inherit", "&:hover": { color: colors.primaryText, textDecoration: "underline" }, "&:focus-visible": { outline: `2px solid ${colors.primary}`, outlineOffset: 2, borderRadius: "2px" } }}
                          >
                            {row.title}
                          </Typography>
                          <Chip
                            label={CONTRACT_TEMPLATE_AUDIENCE_LABELS[row.audience]}
                            size="small"
                            title="A quem o contrato se destina"
                            sx={{ ml: 1, height: 18, fontSize: "0.68rem", fontWeight: 600, backgroundColor: row.audience === "professional" ? "#e7e1f7" : "#e0f5f8", color: row.audience === "professional" ? "#4a2f8f" : "#0b6a77" }}
                          />
                          <Typography sx={{ fontSize: "0.78rem", color: colors.textSecondary, wordBreak: "break-word" }}>
                            {row.description || "Sem descrição"}
                          </Typography>
                        </Box>
                      </Box>
                      <Box className="admin-plans-cell" data-label="Versões" sx={{ flex: "2 1 200px", minWidth: 0, flexWrap: "wrap", gap: 0.5 }}>
                        {row.versions.map(versionChip)}
                      </Box>
                      <Box className="admin-plans-cell c4u-col--md" data-label="Cadastrado por" sx={{ flex: "0 0 180px", minWidth: 0 }}>
                        <Box sx={{ minWidth: 0 }}>
                          <Typography sx={{ fontSize: "0.85rem", color: colors.textPrimary, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }} title={row.createdByName || undefined}>
                            {row.createdByName || "—"}
                          </Typography>
                          <Typography sx={{ fontSize: "0.75rem", color: colors.textSecondary }}>{formatDate(row.createdDate)}</Typography>
                        </Box>
                      </Box>
                      <Box className="admin-plans-cell" data-label="Situação" sx={{ flex: "0 0 90px" }}>
                        <Chip
                          label={row.active ? "Ativo" : "Inativo"}
                          size="small"
                          onClick={() => toggleActive(row)}
                          title={row.active ? `Clique para inativar (some da emissão; ${row.contractsCount} contrato${row.contractsCount === 1 ? "" : "s"} emitido${row.contractsCount === 1 ? "" : "s"} continua${row.contractsCount === 1 ? "" : "m"})` : "Clique para ativar"}
                          sx={{ color: "white", fontWeight: 600, cursor: "pointer", backgroundColor: row.active ? colors.success : colors.textSecondary }}
                        />
                      </Box>
                      <Box className="admin-plans-cell admin-plans-actions" data-label="Ações" sx={{ flex: "0 0 170px", display: "flex", gap: 0.5, justifyContent: "flex-end" }}>
                        <IconButton className="c4u-row-action" size="small" onClick={() => openEditor(row)} title="Editar o modelo (texto, campos e versões)" sx={rowActionSx(colors.primary, colors.primaryHover)}>
                          <Edit sx={{ fontSize: "1rem" }} />
                        </IconButton>
                        <IconButton className="c4u-row-action" size="small" onClick={() => openDuplicate(row)} title="Duplicar: modelo novo e independente, copiado da versão publicada" sx={rowActionSx("#6c757d", "#5a6268")}>
                          <ContentCopy sx={{ fontSize: "1rem" }} />
                        </IconButton>
                        {!hasDraft && (
                          <IconButton className="c4u-row-action" size="small" onClick={() => newVersion(row)} title="Nova versão (cópia da última, em rascunho)" sx={rowActionSx("#17a2b8", "#138496")}>
                            <NoteAdd sx={{ fontSize: "1rem" }} />
                          </IconButton>
                        )}
                        {!hasPublished && row.contractsCount === 0 && (
                          <IconButton className="c4u-row-action" size="small" onClick={() => setDeleteTarget(row)} title="Excluir (modelo nunca publicado nem usado em contrato)" sx={rowActionSx("#dc3545", "#c82333")}>
                            <Delete sx={{ fontSize: "1rem" }} />
                          </IconButton>
                        )}
                      </Box>
                    </Box>
                  );
                })}
              </div>
            )}
          </div>
        </Container>
      </Box>

      <Dialog open={form !== null} onClose={() => setForm(null)} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: "12px", position: "relative", overflow: "hidden" } }}>
        <SavingOverlay open={saving} message="Salvando..." />
        <DialogTitle sx={{ backgroundColor: colors.primary, color: colors.white, display: "flex", justifyContent: "space-between", alignItems: "center", padding: "1rem 1.25rem" }}>
          <Typography component="h3" sx={{ fontSize: "1.1rem", fontWeight: typography.fontWeight.semibold, margin: 0 }}>
            {form?.id ? "Editar modelo de contrato" : "Novo modelo de contrato"}
          </Typography>
          <IconButton onClick={() => setForm(null)} sx={{ color: colors.white, padding: "0.25rem" }}><Close /></IconButton>
        </DialogTitle>
        <DialogContent sx={{ padding: "1.25rem !important", paddingTop: "1.5rem !important" }}>
          {form && (
            <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
              <TextField
                label="Título"
                value={form.title}
                onChange={(e) => setForm({ ...form, title: e.target.value })}
                fullWidth
                size="small"
                inputProps={{ maxLength: MAX_TITLE }}
                InputLabelProps={{ shrink: true }}
                placeholder="Ex.: Contrato de prestação de serviços terapêuticos"
              />
              <TextField
                label="Descrição"
                value={form.description}
                onChange={(e) => setForm({ ...form, description: e.target.value })}
                fullWidth
                size="small"
                multiline
                minRows={2}
                inputProps={{ maxLength: MAX_DESCRIPTION }}
                InputLabelProps={{ shrink: true }}
                placeholder="Para que serve este contrato e quando usá-lo"
              />
              {!form.id && (
                <TextField
                  select
                  label="Destinatário"
                  value={form.audience}
                  onChange={(e) => setForm({ ...form, audience: e.target.value as ContractTemplateAudience })}
                  fullWidth
                  size="small"
                  InputLabelProps={{ shrink: true }}
                  helperText={form.audience === "professional"
                    ? "Contrato com o usuário profissional (vínculo com a clínica): emitido pela lista de Usuários, com as variáveis {profissional.*}."
                    : "Contrato com o paciente ou responsável: emitido na aba Contratos do cadastro, com as variáveis {paciente.*} e {responsavel.*}."}
                >
                  {(Object.keys(CONTRACT_TEMPLATE_AUDIENCE_LABELS) as ContractTemplateAudience[]).map((a) => (
                    <MenuItem key={a} value={a}>{CONTRACT_TEMPLATE_AUDIENCE_LABELS[a]}</MenuItem>
                  ))}
                </TextField>
              )}
              {!form.id && (
                <Typography sx={{ fontSize: "0.8rem", color: colors.textSecondary }}>
                  O modelo nasce com a versão 1 em rascunho. Ao incluir, você vai para o editor do texto.
                </Typography>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ padding: "1rem 1.25rem" }}>
          <Button onClick={() => setForm(null)} variant="outlined" sx={{ textTransform: "none", color: "#6c757d", borderColor: "#6c757d" }}>Cancelar</Button>
          <Button onClick={saveHeader} variant="contained" disabled={saving} sx={{ textTransform: "none", backgroundColor: colors.primary }}>
            {saving ? "Salvando..." : form?.id ? "Salvar" : "Incluir"}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={duplicateTarget !== null} onClose={() => setDuplicateTarget(null)} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: "12px", position: "relative", overflow: "hidden" } }}>
        <SavingOverlay open={saving} message="Duplicando..." />
        <DialogTitle sx={{ backgroundColor: colors.primary, color: colors.white, display: "flex", justifyContent: "space-between", alignItems: "center", padding: "1rem 1.25rem" }}>
          <Typography component="h3" sx={{ fontSize: "1.1rem", fontWeight: typography.fontWeight.semibold, margin: 0 }}>Duplicar modelo de contrato</Typography>
          <IconButton onClick={() => setDuplicateTarget(null)} sx={{ color: colors.white, padding: "0.25rem" }}><Close /></IconButton>
        </DialogTitle>
        <DialogContent sx={{ padding: "1.25rem !important", paddingTop: "1.5rem !important" }}>
          <Typography sx={{ fontSize: "0.85rem", color: colors.textSecondary, mb: 2 }}>
            Cria um modelo <strong>novo e independente</strong> a partir de <strong>{duplicateTarget?.title}</strong>: o texto, os campos e o template da versão publicada (ou da mais recente) viram a v1 em rascunho do novo modelo. Para mudar o mesmo contrato mantendo o histórico, use Nova versão.
          </Typography>
          <TextField
            label="Título do novo modelo"
            value={duplicateTitle}
            onChange={(e) => setDuplicateTitle(e.target.value)}
            fullWidth
            size="small"
            autoFocus
            inputProps={{ maxLength: MAX_TITLE }}
            InputLabelProps={{ shrink: true }}
          />
        </DialogContent>
        <DialogActions sx={{ padding: "1rem 1.25rem" }}>
          <Button onClick={() => setDuplicateTarget(null)} variant="outlined" sx={{ textTransform: "none", color: "#6c757d", borderColor: "#6c757d" }}>Cancelar</Button>
          <Button onClick={confirmDuplicate} variant="contained" disabled={saving || !duplicateTitle.trim()} sx={{ textTransform: "none", backgroundColor: colors.primary }}>
            Duplicar
          </Button>
        </DialogActions>
      </Dialog>

      <ConfirmModal
        isOpen={deleteTarget !== null}
        title="Excluir modelo de contrato"
        message={`Excluir "${deleteTarget?.title ?? ""}" e o rascunho dele? Só é possível porque nenhuma versão foi publicada.`}
        type="warning"
        confirmButtonText="Excluir"
        cancelButtonText="Cancelar"
        isProcessing={saving}
        onConfirm={confirmDelete}
        onCancel={() => setDeleteTarget(null)}
      />

      <FooterInternal className="login-footer-component" />
      <Toast message={toast.message} type={toast.type} isVisible={toast.isVisible} onClose={hideToast} />
    </Box>
  );
};

export default ContractTemplates;
